export interface DataTablePersistedState {
  page: number;
  perPage: number;
  visibleColumnKeys?: string[];
  columnKeys?: string[];
  columnOrderKeys?: string[];
  updatedAt: number;
}

const STORAGE_PREFIX = 'suite241_gestion_commerciale_datatable_state_v1';

const hasStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizePositiveInteger = (value: unknown): number | null => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue) || numberValue < 1) {
    return null;
  }

  return Math.floor(numberValue);
};

const normalizeColumnKeys = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  return Array.from(new Set(value
    .map((key) => String(key || '').trim())
    .filter(Boolean)));
};

const sanitizeKeyPart = (value: unknown, fallback: string): string => {
  const normalized = String(value ?? '').trim();

  return (normalized || fallback).replace(/[^a-zA-Z0-9_.-]/g, '_');
};

const resolveUserId = (store: unknown): string => {
  if (!isRecord(store)) return 'guest';

  const getters = isRecord(store.getters) ? store.getters : {};
  const state = isRecord(store.state) ? store.state : {};
  const authState = isRecord(state.auth) ? state.auth : {};
  const getterUser = getters['auth/currentUser'];
  const stateUser = authState.user;
  const user = isRecord(getterUser) ? getterUser : stateUser;

  return sanitizeKeyPart(isRecord(user) ? user.id : null, 'guest');
};

const resolveShopId = (store: unknown): string => {
  if (!isRecord(store)) return 'no-shop';

  const getters = isRecord(store.getters) ? store.getters : {};
  const state = isRecord(store.state) ? store.state : {};
  const shopState = isRecord(state.shop) ? state.shop : {};
  const shopId = getters['shop/currentShopId'] ?? shopState.currentShopId;

  return sanitizeKeyPart(shopId, 'no-shop');
};

export const dataTableStateStorageKey = (tableKey: string, store?: unknown): string =>
  [
    STORAGE_PREFIX,
    resolveUserId(store),
    resolveShopId(store),
    sanitizeKeyPart(tableKey, 'table'),
  ].join(':');

export const normalizeDataTableState = (value: unknown): DataTablePersistedState | null => {
  if (!isRecord(value)) return null;

  const page = normalizePositiveInteger(value.page);
  const perPage = normalizePositiveInteger(value.perPage);

  if (!page || !perPage) return null;

  const visibleColumnKeys = normalizeColumnKeys(value.visibleColumnKeys);
  const columnKeys = normalizeColumnKeys(value.columnKeys);
  const columnOrderKeys = normalizeColumnKeys(value.columnOrderKeys);

  return {
    page,
    perPage,
    ...(visibleColumnKeys ? { visibleColumnKeys } : {}),
    ...(columnKeys ? { columnKeys } : {}),
    ...(columnOrderKeys ? { columnOrderKeys } : {}),
    updatedAt: Number.isFinite(Number(value.updatedAt)) ? Number(value.updatedAt) : Date.now(),
  };
};

export const loadDataTableState = (
  tableKey: string,
  store?: unknown
): DataTablePersistedState | null => {
  if (!hasStorage() || !tableKey) return null;

  try {
    const raw = window.localStorage.getItem(dataTableStateStorageKey(tableKey, store));
    return raw ? normalizeDataTableState(JSON.parse(raw)) : null;
  } catch (error) {
    console.warn('État du tableau illisible', error);
    return null;
  }
};

export const saveDataTableState = (
  tableKey: string,
  state: Pick<DataTablePersistedState, 'page' | 'perPage'> & Partial<Pick<DataTablePersistedState, 'visibleColumnKeys' | 'columnKeys' | 'columnOrderKeys'>>,
  store?: unknown
): void => {
  if (!hasStorage() || !tableKey) return;

  const normalized = normalizeDataTableState({
    ...state,
    updatedAt: Date.now(),
  });

  if (!normalized) return;

  try {
    window.localStorage.setItem(
      dataTableStateStorageKey(tableKey, store),
      JSON.stringify(normalized)
    );
  } catch (error) {
    console.warn('État du tableau non enregistré', error);
  }
};
